// Pure, timezone-aware matcher for the auto-clockout cron (#10, ADR 0010).
//
// A Club configures a wall-clock close time-of-day (`auto_clockout_at`, e.g.
// `01:00`) in its IANA `timezone`. The cron fires on a fixed UTC cadence and,
// for each Club, must decide: did this Club's close moment fall in the window
// since the previous tick? We use a half-open window `(now - windowMs, now]`
// rather than exact equality, for two reasons:
//
//   1. Not every timezone is a whole-hour offset (India is +5:30, parts of
//      Australia +9:30), so a UTC-aligned cron rarely lands on a whole local
//      minute. A "crossed since the last tick" window is correct regardless of
//      offset; exact-equality matching would silently never fire in +5:30 zones.
//   2. The window fires the reset exactly once per close event (the bulk reset
//      is idempotent, but the window also prevents re-clearing a Talent who
//      clocked back in after close until the next distinct close event).
//
// All math is native `Intl` (no date-fns — deliberate deviation from ADR 0001,
// recorded in ADR 0010). The one non-obvious primitive is converting a local
// wall-clock time in a zone to a UTC instant; we derive the zone offset for an
// instant via `Intl.DateTimeFormat` and correct once for DST boundaries.

/**
 * True iff `tz` is a canonical IANA timezone name that `Intl` will apply DST
 * rules to. Fixed-offset abbreviations (`MST`, `PST`) are rejected: they do NOT
 * observe DST (`MST` is UTC−7 year-round), so a Club configured with one is an
 * hour off half the year — only IANA `Area/Location` names carry the DST rules.
 *
 * IANA names contain a `/` (or are the special `UTC`); abbreviations do not.
 * `Intl.supportedValuesOf('timeZone')` is unreliable across runtimes (some omit
 * `UTC`/`Asia/Kolkata`), so we combine the shape check with a construction probe
 * that rejects made-up `Area/Location` strings.
 */
export function isIanaTimeZone(tz: string): boolean {
	if (!tz.includes('/') && tz !== 'UTC') return false;
	try {
		new Intl.DateTimeFormat('en-US', { timeZone: tz });
		return true;
	} catch {
		return false;
	}
}

interface TimeOfDay {
	h: number;
	m: number;
	s: number;
}

/** Parse `HH:MM` or `HH:MM:SS` (Postgres `time`) into components, or null. */
function parseTimeOfDay(value: string): TimeOfDay | null {
	const parts = value.split(':');
	if (parts.length < 2) return null;
	const h = Number(parts[0]);
	const m = Number(parts[1]);
	const s = parts[2] === undefined ? 0 : Number(parts[2]);
	if (!Number.isInteger(h) || !Number.isInteger(m) || !Number.isInteger(s)) {
		return null;
	}
	if (h < 0 || h > 23 || m < 0 || m > 59 || s < 0 || s > 59) return null;
	return { h, m, s };
}

function zonedParts(
	timeZone: string,
	instant: Date,
): {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
} {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hourCycle: 'h23',
	}).formatToParts(instant);
	const get = (type: string): number =>
		Number(parts.find((p) => p.type === type)?.value);
	return {
		year: get('year'),
		month: get('month'),
		day: get('day'),
		hour: get('hour'),
		minute: get('minute'),
		second: get('second'),
	};
}

/** The zone's offset (localWallClock − UTC) in ms for the given instant. */
function zoneOffsetMs(timeZone: string, instant: Date): number {
	const p = zonedParts(timeZone, instant);
	const asUtc = Date.UTC(
		p.year,
		p.month - 1,
		p.day,
		p.hour,
		p.minute,
		p.second,
	);
	return asUtc - instant.getTime();
}

/**
 * The UTC epoch-ms for a local wall-clock time in `timeZone`. `Date.UTC`
 * normalizes day overflow/underflow (day 0 → prior month's last day), so
 * callers can pass `day ± 1` freely. One refinement pass handles DST offset
 * changes around the target instant.
 */
function zonedWallClockToUtcMs(
	timeZone: string,
	year: number,
	month: number,
	day: number,
	time: TimeOfDay,
): number {
	const naive = Date.UTC(year, month - 1, day, time.h, time.m, time.s);
	let utc = naive - zoneOffsetMs(timeZone, new Date(naive));
	utc = naive - zoneOffsetMs(timeZone, new Date(utc));
	return utc;
}

/**
 * True iff the Club's close moment (`closeAt` wall-clock in `timeZone`) fell in
 * the half-open window `(now - windowMs, now]`. Returns false for an unparseable
 * `closeAt`. `windowMs` is the cron cadence (e.g. one hour).
 *
 * Checks the close occurrence on the local day of `now` and its neighbours
 * (±1 day) so midnight-wrap and month boundaries are handled without special
 * cases; with a sub-day window at most one candidate can fall inside it.
 */
export function shouldResetAt(
	timeZone: string,
	closeAt: string,
	now: Date,
	windowMs: number,
): boolean {
	const time = parseTimeOfDay(closeAt);
	if (!time) return false;
	const { year, month, day } = zonedParts(timeZone, now);
	const nowMs = now.getTime();
	const lowerExclusive = nowMs - windowMs;
	for (const dayOffset of [-1, 0, 1]) {
		const closeMs = zonedWallClockToUtcMs(
			timeZone,
			year,
			month,
			day + dayOffset,
			time,
		);
		if (closeMs > lowerExclusive && closeMs <= nowMs) return true;
	}
	return false;
}

/**
 * Epoch-ms of the next occurrence of `closeAt` (wall-clock in `timeZone`) at or
 * after `now`, or null for an unparseable `closeAt`. Used by the countdown
 * banner to render "in Nh Mm" — the same zoned math as {@link shouldResetAt}, so
 * the UI and the cron agree on when a Club's close is.
 */
export function nextCloseAtMs(
	timeZone: string,
	closeAt: string,
	now: Date,
): number | null {
	const time = parseTimeOfDay(closeAt);
	if (!time) return null;
	const { year, month, day } = zonedParts(timeZone, now);
	const nowMs = now.getTime();
	let best: number | null = null;
	// Today's occurrence may already be in the past; the next is tomorrow's.
	// The +2 candidate is defensive around DST/month boundaries.
	for (const dayOffset of [0, 1, 2]) {
		const ms = zonedWallClockToUtcMs(
			timeZone,
			year,
			month,
			day + dayOffset,
			time,
		);
		if (ms >= nowMs && (best === null || ms < best)) best = ms;
	}
	return best;
}
